// src/lib/quiz.ts — Quiz question generation

import type { Question, QuizConfig, QuizQuestion, QuizOption, AnswerBlock } from '@/types';

export function generateQuizQuestions(
  allQuestions: Question[],
  config: QuizConfig
): QuizQuestion[] {
  // Filter by category and difficulty range
  let pool = allQuestions.filter((q) => {
    const catMatch = config.category === 'mixed' || q.category === config.category;
    const diffMatch = q.difficulty >= config.difficultyMin && q.difficulty <= config.difficultyMax;
    return catMatch && diffMatch;
  });

  // Fisher-Yates shuffle
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }

  pool = pool.slice(0, config.questionCount);

  // Generate 4 options per question (1 correct + 3 distractors from same category)
  return pool.map((q): QuizQuestion => {
    const correctOption: QuizOption = { id: 'a', text: q.shortAnswer };

    const distractors = allQuestions
      .filter((d) => d.id !== q.id && d.category === q.category)
      .sort(() => Math.random() - 0.5)
      .slice(0, 3)
      .map((d, i): QuizOption => ({
        id: (['b', 'c', 'd'] as const)[i] ?? 'b',
        text: d.shortAnswer,
      }));

    // If not enough distractors, pad with cross-category
    while (distractors.length < 3) {
      const crossCat = allQuestions
        .filter((d) => d.id !== q.id && !distractors.some((dd) => dd.text === d.shortAnswer))
        .sort(() => Math.random() - 0.5)[0];
      if (crossCat) {
        const idx = distractors.length;
        distractors.push({
          id: (['b', 'c', 'd'] as const)[idx] ?? 'b',
          text: crossCat.shortAnswer,
        });
      } else {
        break;
      }
    }

    const options = [correctOption, ...distractors].sort(() => Math.random() - 0.5);
    const correctId = options.find((o) => o.text === correctOption.text)?.id ?? 'a';

    // Explanation from interviewTip or first AnswerBlock
    let explanation = q.interviewTip ?? '';
    if (!explanation && q.fullAnswer.length > 0) {
      const firstBlock = q.fullAnswer[0];
      if (firstBlock && 'content' in firstBlock) {
        const content = (firstBlock as AnswerBlock).content;
        explanation = typeof content === 'string' ? content : content[0] ?? '';
      }
    }

    return {
      questionId: q.id,
      questionText: q.title,
      options,
      correctOptionId: correctId,
      explanation,
      difficulty: q.difficulty,
      category: q.category,
      xpReward: q.xpReward,
    };
  });
}
